import { useTranslation } from 'react-i18next';

import type { AcceptedTerms } from '@entities/handover';
import { money } from '@shared/lib/money';

import styles from './Handover.module.css';

interface AcceptedTermsCardProps {
  terms: AcceptedTerms | null;
  locale: string;
}

/**
 * Зафиксированные условия принятого предложения (FE-260, MVP_PRD §39.1).
 *
 * Три правила блока:
 *
 * * **под каждой позицией — цитата автохауса.** Поле без цитаты не заполняется
 *   вовсе (§23.9), а позиция сверх цены без цитаты не существует (§42.3);
 * * **подпись «со слов автохауса, CarViQ.DE не проверял» обязательна.** Без неё
 *   утверждение продавца читается как утверждение продукта (§2);
 * * **формулировку обещания не переписываем.** Показывается то, что написал
 *   автохаус: своего названия у обещания нет.
 *
 * Разница с ценой объявления даётся **со знаком**: «−1 500 €» и «1 500 €» —
 * разные факты.
 */
export function AcceptedTermsCard({ terms, locale }: AcceptedTermsCardProps) {
  const { t } = useTranslation();

  if (terms === null) {
    return (
      <section className={styles.section} aria-labelledby="handover-terms">
        <h2 id="handover-terms" className={styles.title}>
          {t('handover.terms.title')}
        </h2>
        <p className={styles.note}>{t('handover.terms.missing')}</p>
      </section>
    );
  }

  const delta = terms.price_cents === null ? null : terms.price_cents - terms.listing_price_cents;

  return (
    <section className={styles.section} aria-labelledby="handover-terms">
      <h2 id="handover-terms" className={styles.title}>
        {t('handover.terms.title')}
      </h2>
      <p className={styles.subtitle}>{t('handover.terms.subtitle')}</p>

      <dl className={styles.facts}>
        <div>
          <dt>{t('handover.terms.listingPrice')}</dt>
          <dd className="tabular">
            {money(terms.listing_price_cents, terms.currency, locale, 'auto')}
          </dd>
        </div>
        <div>
          <dt>{t('handover.terms.finalPrice')}</dt>
          <dd className={`tabular ${styles.strong}`}>
            {terms.price_cents === null
              ? t('common.noData')
              : money(terms.price_cents, terms.currency, locale, 'auto')}
          </dd>
        </div>
        <div>
          <dt>{t('handover.terms.delta')}</dt>
          <dd className="tabular">
            {delta === null ? t('common.noData') : money(delta, terms.currency, locale, 'auto')}
          </dd>
        </div>
        {terms.total_cost_cents === null ? null : (
          <div>
            <dt>{t('handover.terms.totalCost')}</dt>
            <dd className="tabular">
              {money(terms.total_cost_cents, terms.currency, locale, 'auto')}
            </dd>
          </div>
        )}
        {terms.valid_until === null ? null : (
          <div>
            <dt>{t('handover.terms.validUntil')}</dt>
            <dd>{new Date(terms.valid_until).toLocaleDateString(locale)}</dd>
          </div>
        )}
      </dl>

      {terms.final_quote === null ? null : (
        <blockquote className={styles.quote}>«{terms.final_quote}»</blockquote>
      )}

      {terms.promised.length === 0 ? null : (
        <>
          <h3 className={styles.memoTitle}>{t('handover.terms.promised')}</h3>
          <ul className={styles.quotes}>
            {terms.promised.map((item) => (
              <li key={`${item.label}:${item.quote}`}>
                <span className={styles.label}>{item.label}</span>
                <blockquote className={styles.quote}>«{item.quote}»</blockquote>
              </li>
            ))}
          </ul>
        </>
      )}

      {terms.additional_costs.length === 0 ? null : (
        <>
          <h3 className={styles.memoTitle}>{t('handover.terms.additionalCosts')}</h3>
          <ul className={styles.quotes}>
            {terms.additional_costs.map((item) => (
              <li key={`${item.label}:${item.quote}`}>
                <span className={styles.label}>{item.label}</span>
                <blockquote className={styles.quote}>«{item.quote}»</blockquote>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Обязательная подпись под любой цитатой автохауса (§2, §36.1). */}
      <p className={styles.note}>{t('handover.quoteNote')}</p>
    </section>
  );
}
